// src/pages/redress/RedressTab.tsx
//
// Container for the FCA Motor Finance Redress Scheme UI. Mounted inside
// the existing claim detail surface as a single tab. On mount we look up
// an existing redress case; if none exists, we offer a "Create redress
// case" button. Once a case is present we render sub-screens with an
// agreement selector so the operator can switch between the two agreements
// on a vehicle.
//
// Gated on ``REACT_APP_REDRESS_ENABLED=true`` so the feature can be
// dark-launched in production without a backend flag flip.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  RedressBackendDisabledError,
  RedressCase,
  redressApi,
} from '../../api/redress';

import CalculationsScreen from './CalculationsScreen';
import LedgerScreen from './LedgerScreen';
import LiabilityScreen from './LiabilityScreen';
import ObjectionScreen from './ObjectionScreen';
import OffersScreen from './OffersScreen';
import {
  bannerDangerStyle,
  bannerWarnStyle,
  buttonPrimary,
  buttonSecondary,
  colors,
  humaniseStatus,
  pillForStatus,
  applyDisabled,
  inputStyle,
} from './styles';

type SubTab = 'liability' | 'objection' | 'calculations' | 'offers' | 'ledger';

interface RedressTabProps {
  claimId: string;
}

const REDRESS_ENABLED: boolean =
  (process.env.REACT_APP_REDRESS_ENABLED ?? 'true').toLowerCase() === 'true';

const TAB_ORDER: ReadonlyArray<{ id: SubTab; label: string }> = [
  { id: 'liability', label: 'Liability' },
  { id: 'objection', label: 'Objection' },
  { id: 'calculations', label: 'Calculations' },
  { id: 'offers', label: 'Offers' },
  { id: 'ledger', label: 'Ledger' },
];

export default function RedressTab({ claimId }: RedressTabProps): React.ReactElement {
  const [loading, setLoading] = useState<boolean>(true);
  const [creating, setCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // M14: the frontend flag defaults ON while the backend flag defaults
  // OFF — a backend with REDRESS_ENABLED=false 404s every redress route,
  // which must NOT render as "No redress case yet → Create".
  const [backendDisabled, setBackendDisabled] = useState<boolean>(false);
  const [redressCase, setRedressCase] = useState<RedressCase | null>(null);
  const [subTab, setSubTab] = useState<SubTab>('liability');
  const [agreementIdx, setAgreementIdx] = useState<number>(0);
  const tabRefs = useRef<Record<SubTab, HTMLButtonElement | null>>({
    liability: null,
    objection: null,
    calculations: null,
    offers: null,
    ledger: null,
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setBackendDisabled(false);
    try {
      const result = await redressApi.getByClaim(claimId);
      setRedressCase(result);
      setAgreementIdx(0);
    } catch (err: unknown) {
      if (err instanceof RedressBackendDisabledError) {
        setBackendDisabled(true);
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to load redress case';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    if (!REDRESS_ENABLED) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [refresh]);

  const handleCreate = async (): Promise<void> => {
    setCreating(true);
    setError(null);
    try {
      const created = await redressApi.createForClaim(claimId);
      setRedressCase(created);
    } catch (err: unknown) {
      // Axios error payloads sit under ``response.data`` -- surface the
      // server-side ``error`` / ``reason`` so the UI explains ineligibility.
      const axErr = err as {
        response?: { data?: { error?: string; reason?: string } };
        message?: string;
      };
      const serverMsg =
        axErr.response?.data?.error ||
        axErr.response?.data?.reason ||
        axErr.message ||
        'Failed to create redress case';
      setError(serverMsg);
    } finally {
      setCreating(false);
    }
  };

  // Keyboard arrow navigation for the tablist (WAI-ARIA Authoring Practices).
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>): void => {
      if (
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight' &&
        e.key !== 'Home' &&
        e.key !== 'End'
      ) {
        return;
      }
      e.preventDefault();
      const idx = TAB_ORDER.findIndex((t) => t.id === subTab);
      let nextIdx = idx;
      if (e.key === 'ArrowLeft')
        nextIdx = (idx - 1 + TAB_ORDER.length) % TAB_ORDER.length;
      else if (e.key === 'ArrowRight') nextIdx = (idx + 1) % TAB_ORDER.length;
      else if (e.key === 'Home') nextIdx = 0;
      else if (e.key === 'End') nextIdx = TAB_ORDER.length - 1;
      const next = TAB_ORDER[nextIdx].id;
      setSubTab(next);
      // focus shift happens after render via the ref
      requestAnimationFrame(() => {
        tabRefs.current[next]?.focus();
      });
    },
    [subTab],
  );

  // Derive the active agreement, clamping the index defensively.
  const agreements = redressCase?.agreements ?? [];
  const safeIdx = Math.min(agreementIdx, Math.max(0, agreements.length - 1));
  const activeAgreement = agreements[safeIdx] ?? null;

  const activePanel = useMemo(() => {
    if (!redressCase || !activeAgreement) return null;
    // H12 (P2-13): key every sub-screen by the agreement id. The screens
    // seed their edit drafts in useState INITIALIZERS (they run once per
    // mounted component), so switching agreements without a remount left
    // agreement A's draft bleeding into agreement B. The key forces a
    // fresh mount — and fresh initializers — per agreement. React 19:
    // ``key`` must be passed explicitly on the JSX element, never inside
    // a spread object (spread keys are ignored with a warning).
    const key = activeAgreement.agreementId;
    const common = {
      claimId,
      redressCase,
      agreement: activeAgreement,
      agreementId: activeAgreement.agreementId,
      onCaseUpdated: setRedressCase,
    };
    switch (subTab) {
      case 'liability':
        return <LiabilityScreen key={key} {...common} />;
      case 'objection':
        return <ObjectionScreen key={key} {...common} />;
      case 'calculations':
        return <CalculationsScreen key={key} {...common} />;
      case 'offers':
        return <OffersScreen key={key} {...common} />;
      case 'ledger':
        return <LedgerScreen key={key} {...common} />;
    }
  }, [claimId, redressCase, activeAgreement, subTab]);

  if (!REDRESS_ENABLED) {
    return (
      <div style={{ padding: 16 }}>
        <em style={{ color: colors.neutral500 }}>
          Redress scheme is disabled in this environment.
        </em>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 24, color: colors.neutral600 }}>
        <Spinner />
        <span style={{ marginLeft: 10 }}>Loading redress case…</span>
      </div>
    );
  }

  // M14: backend REDRESS_ENABLED=false — the routes don't exist. Never
  // offer "Create redress case" here (it could only 404 too).
  if (backendDisabled) {
    return (
      <div style={{ padding: 24 }}>
        <div style={bannerWarnStyle} role="status">
          <strong>Redress module is not enabled on the server.</strong>
          <div style={{ marginTop: 4, color: colors.neutral700 }}>
            The backend is running with <code>REDRESS_ENABLED=false</code>,
            so redress cases cannot be loaded or created from this
            environment.
          </div>
        </div>
      </div>
    );
  }

  if (error && redressCase === null) {
    return (
      <div style={{ padding: 24 }}>
        <div style={bannerDangerStyle} role="alert">
          <strong>Could not load redress case.</strong>
          <div style={{ marginTop: 4, color: colors.neutral700 }}>{error}</div>
        </div>
        <button
          onClick={() => void refresh()}
          style={buttonSecondary}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (redressCase === null) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: 'center',
          maxWidth: 480,
          margin: '24px auto',
          border: `1px dashed ${colors.neutral300}`,
          borderRadius: 12,
          background: colors.neutral50,
        }}
      >
        <div style={{ fontSize: 36, marginBottom: 8 }} aria-hidden="true">
          {/* Neutral folder glyph -- no emoji library available */}
          ⬚
        </div>
        <h3 style={{ margin: '0 0 8px', color: colors.neutral800 }}>
          No redress case yet
        </h3>
        <p style={{ margin: '0 0 16px', color: colors.neutral600, fontSize: 14 }}>
          This claim has not been opened under the FCA Motor Finance Redress
          Scheme. Create a case to begin liability assessment.
        </p>
        <button
          onClick={() => void handleCreate()}
          disabled={creating}
          style={applyDisabled(buttonPrimary, creating)}
          aria-busy={creating}
          type="button"
        >
          {creating ? 'Creating…' : 'Create redress case'}
        </button>
        {error !== null && (
          <div style={{ ...bannerDangerStyle, marginTop: 16 }} role="alert">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 0 }}>
      <header style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              color: colors.neutral900,
            }}
          >
            Redress case
          </h3>
          <code style={refCodeStyle}>{redressCase.proclaimReference}</code>
          {redressCase.vehicle?.registration ? (
            <span style={{ color: colors.neutral600, fontSize: 13, fontWeight: 600 }}>
              {[redressCase.vehicle.make, redressCase.vehicle.model]
                .filter(Boolean)
                .join(' ')}{' '}
              ({redressCase.vehicle.registration})
            </span>
          ) : null}
          <span
            /* P0-4: caseStatus can be undefined on malformed cases —
               guard so pillForStatus/.split can't crash the tab. */
            style={pillForStatus(redressCase.caseStatus ?? '')}
            aria-label={`Case status: ${redressCase.caseStatus ?? 'unknown'}`}
          >
            {humaniseStatus(redressCase.caseStatus)}
          </span>
        </div>
      </header>

      {/* Agreement selector strip */}
      {redressCase && agreements.length === 0 ? (
        <div style={bannerWarnStyle} role="alert">
          This redress case has no agreements.
        </div>
      ) : agreements.length > 1 ? (
        <div style={agreementBarStyle}>
          <label style={{ fontSize: 13, fontWeight: 600, color: colors.neutral700 }}>
            Agreement
            <select
              value={safeIdx}
              onChange={(e) => setAgreementIdx(Number(e.target.value))}
              style={{ ...inputStyle, width: 'auto', marginLeft: 8, minWidth: 240 }}
            >
              {agreements.map((a, i) => (
                <option key={a.agreementId} value={i}>
                  {(a.lenderDetails?.lenderName || 'Agreement') +
                    (a.lenderDetails?.agreementNumber
                      ? ` · ${a.lenderDetails.agreementNumber}`
                      : ` ${i + 1}`) +
                    ` — ${humaniseStatus(a.workflow.status)}`}
                </option>
              ))}
            </select>
          </label>
          <span style={{ fontSize: 12, color: colors.neutral500 }}>
            {agreements.length} agreements on this vehicle
          </span>
        </div>
      ) : activeAgreement ? (
        <div style={agreementBarStyle}>
          <span style={{ fontSize: 13, color: colors.neutral600 }}>
            {activeAgreement.lenderDetails?.lenderName || 'Single agreement'}
            {activeAgreement.lenderDetails?.agreementNumber
              ? ` · ${activeAgreement.lenderDetails.agreementNumber}`
              : ''}
          </span>
          <span
            style={pillForStatus(activeAgreement.workflow.status)}
            aria-label={`Agreement status: ${activeAgreement.workflow.status}`}
          >
            {humaniseStatus(activeAgreement.workflow.status)}
          </span>
        </div>
      ) : null}

      <nav
        role="tablist"
        aria-label="Redress case sections"
        style={subTabBarStyle}
      >
        {TAB_ORDER.map((t) => {
          const isActive = subTab === t.id;
          return (
            <button
              key={t.id}
              ref={(el) => {
                tabRefs.current[t.id] = el;
              }}
              type="button"
              role="tab"
              id={`redress-tab-${t.id}`}
              aria-selected={isActive}
              aria-controls={`redress-panel-${t.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setSubTab(t.id)}
              onKeyDown={handleTabKeyDown}
              style={isActive ? subTabActiveStyle : subTabStyle}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <div
        role="tabpanel"
        id={`redress-panel-${subTab}`}
        aria-labelledby={`redress-tab-${subTab}`}
        style={{ padding: '4px 0' }}
      >
        {activePanel}
      </div>
    </div>
  );
}

// --- Small helpers ---------------------------------------------------------

function Spinner(): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: 16,
        height: 16,
        border: `2px solid ${colors.neutral200}`,
        borderTopColor: colors.primary,
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        verticalAlign: 'middle',
      }}
    />
  );
}

// --- Inline styles ---------------------------------------------------------

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 16,
  paddingRight: 56, // keep clear of the Modal's top-right close button (40px + gutter)
  flexWrap: 'wrap',
};

const refCodeStyle: React.CSSProperties = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 13,
  background: colors.neutral100,
  color: colors.neutral800,
  padding: '2px 8px',
  borderRadius: 4,
  border: `1px solid ${colors.neutral200}`,
};

const subTabBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  borderBottom: `1px solid ${colors.neutral200}`,
  marginBottom: 16,
};

const subTabStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  borderBottom: '2px solid transparent',
  padding: '10px 16px',
  cursor: 'pointer',
  color: colors.neutral600,
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  marginBottom: -1,
};

const subTabActiveStyle: React.CSSProperties = {
  ...subTabStyle,
  color: colors.primary,
  borderBottomColor: colors.primary,
  fontWeight: 600,
};

const agreementBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  padding: '8px 0 12px',
  marginBottom: 4,
};
